import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  addTradingGroupCredentials,
  createUser,
  createTradingGroup,
  deleteTradingGroup,
  getUsers,
  getAccounts,
  getTradingGroups,
  removeGroupMember,
  removeTradingGroupCredentials,
  deleteUser as apiDeleteUser,
  updateGroupMember,
  updateTradingGroup,
  upsertGroupMember,
  updateUser,
} from '@/apiServices';

const AdminPanelDataContext = createContext();

export function AdminPanelDataProvider({ children }) {
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [groupsResp, usersResp, accountsResp] = await Promise.all([
        getTradingGroups(),
        getUsers({ include_memberships: true }),
        getAccounts(),
      ]);
      setGroups(groupsResp || []);
      setUsers(usersResp || []);
      setAccounts(accountsResp || []);
    } catch (e) {
      setError(e?.message || 'Failed to load groups');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const createGroup = useCallback(
    async ({ name, description, accountIds = [], initialMembers = [] }) => {
      await createTradingGroup({
        name,
        description,
        exchange_credential_ids: accountIds,
        memberships: initialMembers,
      });
      await load();
    },
    [load]
  );

  const patchGroup = useCallback(
    async (groupId, updates) => {
      await updateTradingGroup(groupId, updates);
      await load();
    },
    [load]
  );

  const attachCredentials = useCallback(
    async (groupId, exchangeCredentialIds) => {
      await addTradingGroupCredentials(groupId, exchangeCredentialIds);
      await load();
    },
    [load]
  );

  const detachCredentials = useCallback(
    async (groupId, exchangeCredentialIds) => {
      await removeTradingGroupCredentials(groupId, exchangeCredentialIds);
      await load();
    },
    [load]
  );

  const removeGroup = useCallback(
    async (groupId) => {
      await deleteTradingGroup(groupId);
      await load();
    },
    [load]
  );

  const addOrUpdateMember = useCallback(
    async ({ groupId, userId, permissions }) => {
      await upsertGroupMember(groupId, userId, permissions);
      await load();
    },
    [load]
  );

  const setMemberPermissions = useCallback(
    async ({ groupId, userId, permissions }) => {
      await updateGroupMember(groupId, userId, permissions);
      await load();
    },
    [load]
  );

  const removeMember = useCallback(
    async ({ groupId, userId }) => {
      await removeGroupMember(groupId, userId);
      await load();
    },
    [load]
  );

  const editUser = useCallback(
    async ({ user_id, changes }) => {
      await updateUser(user_id, changes);
      await load();
    },
    [load]
  );

  const deleteUser = useCallback(
    async ({ userId }) => {
      await apiDeleteUser(userId);
      await load();
    },
    [load]
  );

  const value = useMemo(
    () => ({
      groups,
      users,
      accounts,
      isLoading,
      error,
      reload: load,
      createUser,
      editUser,
      deleteUser,
      createGroup,
      patchGroup,
      removeGroup,
      addOrUpdateMember,
      setMemberPermissions,
      removeMember,
      attachCredentials,
      detachCredentials,
    }),
    [
      groups,
      users,
      accounts,
      isLoading,
      error,
      load,
      createGroup,
      patchGroup,
      removeGroup,
      addOrUpdateMember,
      setMemberPermissions,
      removeMember,
      deleteUser,
      attachCredentials,
      detachCredentials,
    ]
  );

  return <AdminPanelDataContext.Provider value={value}>{children}</AdminPanelDataContext.Provider>;
}

export const useAdminPanelData = () => useContext(AdminPanelDataContext);
